import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Input from '@components/Forms/TextField';
import SubmitAndBack from '@components/Forms/SubmitAndBack';
import * as Routes from '@constants/routes';
import GenericForm from '@components/Forms/GenericForm';
import LoginHeader from '@components/LoginHeader';
import Card from '@components/Cards/ScreenCard';
import { makeStyles } from '@material-ui/core/styles';

import { sendResetPasswordEmail } from '@services/login-service';

import { setRecoverEmail, setToken } from '@store/login/actions';

import Header from '@components/Forms/Header';
import useBreakpoint from '@hooks/breakpoint';
import useCommon from '@hooks/useCommon';
import { useHistory } from 'react-router';
import schema from './schema';

const Container = styled.div`
  padding-top: 50px;
  padding-left: 24px;
  padding-right: 24px;
  ${({ theme }) => theme.breakpoint('sm', 'md')} {
    padding: unset;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
`;

const InputText = styled(Input)`
  margin-bottom: 0;
`;

const helperTextStyles = makeStyles(() => ({
  error: {
    '&.MuiFormHelperText-root.Mui-error': {
      color: '#E40046',
    },
  },
}));

const EmailResetForm = (props) => {
  const { email: reduxStateEmail } = useSelector((state) => state.login);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { findEmail, pushNewSentEmail, deleteTimerSentByEmail } = useCommon();
  const history = useHistory();
  const [email, setEmail] = useState(reduxStateEmail || '');
  const { mobile } = useBreakpoint();
  const [errorEmail, setErrorEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const helperTestClasses = helperTextStyles();

  const handleIsMerchantRutSuccess = ({ token }) => {
    dispatch(setToken(token));
    dispatch(setRecoverEmail(email));
    history.push(`${Routes.EMAIL_SENT}`);
  };

  const handleIsMerchantRutError = () => {
    setErrorEmail(true);
    deleteTimerSentByEmail(email);
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      const { token } = props;
      const timerEmailSend = findEmail(email);
      if (!timerEmailSend) {
        pushNewSentEmail({ email, token });
        sendResetPasswordEmail(
          { email, token },
          handleIsMerchantRutSuccess,
          handleIsMerchantRutError
        );
      } else {
        pushNewSentEmail({ email, token: timerEmailSend.token });
        dispatch(setToken(timerEmailSend.token));
        dispatch(setRecoverEmail(email));
        !errorMail && history.push(`${Routes.EMAIL_SENT}`);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
      }, 2000);
    }
  };

  const handleBack = () => {
    history.push(Routes.LOGIN);
  };

  return (
    <Container>
      {mobile ? <Header withoutTextMobile /> : <LoginHeader />}
      <Card
        title={t('login.resetPassword.common.title')}
        subtitle={t('login.resetPassword.emailForm.subTitle')}
      >
        <GenericForm schema={schema} item container onSubmit={handleSubmit} xs={12}>
          <InputText
            value={email}
            label="Email"
            onChange={(e) => {
              setEmail(e.target.value);
              setErrorEmail(false);
            }}
            id="email"
            autoComplete="off"
            fullWidth
            variant="outlined"
            gridProps={{ xs: 12, item: true }}
            error={errorEmail}
            FormHelperTextProps={{ classes: helperTestClasses }}
          />
          <SubmitAndBack
            gridProps={{ xs: 12, item: true }}
            submitText={t('login.resetPassword.emailForm.sendInstructions')}
            handleBack={handleBack}
            reset
            handleSubmit={handleSubmit}
            withButton
            isSubmitDisabled={isSubmitting}
          />
        </GenericForm>
      </Card>
    </Container>
  );
};

export default EmailResetForm;
